/**
 * DEV-1110 — the device credential: what lets this app open with no signal.
 *
 * ── Why a session cannot do this ─────────────────────────────────────────────
 *
 * The shell signs in through Auth0 and holds a 24h Metaship bearer that is
 * never persisted — `tokenStore.ts` keeps only the Auth0 REFRESH token, and
 * spending it is a network call. So a cold start with no signal has nothing to
 * present and nothing it can do about it: `resumeFromRefreshToken` fails, the
 * visitor lands on Login.vue, and Login.vue needs the network too. A grower in
 * a block with no bars is locked out of their own records.
 *
 * A device credential is the answer the platform already had. `apiCredential`
 * rows bound to a device (WMS handhelds, IoT units) carry a clientId/secretKey
 * pair, valid for 30 days and self-renewing, exchangeable for a 12h token at
 * `POST /public/v2/auth`. Devin, 2026-09-13: *"we bought that device so that
 * they can work online, offline all the time. And if they want to remove that
 * device, they can go into the settings of their app when they online and go
 * de-auth a device."*
 *
 * ── What this module is careful about ────────────────────────────────────────
 *
 * The secret is a 30-day credential for a real organisation, sitting on a phone
 * that lives in a bakkie. So:
 *   - it is encrypted at rest (`secureStore.ts`), and a browser that cannot
 *     encrypt is simply never enrolled;
 *   - the envelope records WHICH user and organisation it was minted for, and
 *     is refused for anyone else — a shared farm phone must not hand grower B
 *     grower A's credential;
 *   - an explicit sign-out destroys it, and the key with it. Only a transient
 *     failure (`clearRefreshToken: false`) leaves it in place — that case is
 *     precisely "the network is gone", which is what it exists for.
 */

import { ApiRequestError, apiRequest } from "@/auth/session";
import { getInstallId } from "@/device/installId";
import {
  decryptJson,
  destroyKey,
  encryptJson,
  isSecureStoreAvailable,
  SecureStoreUnavailableError,
} from "@/device/secureStore";
import { isFieldApp } from "@/tenant/fieldApp";
import type { ResolvedMfe } from "@/tenant/types";

const CREDENTIAL_KEY = "metaship:deviceCredential";

/**
 * Renew once the credential is inside this window of its expiry. Seven days of
 * a thirty-day life: long enough that a phone which sees signal roughly weekly
 * never lapses, short enough not to rotate the secret on every sign-in.
 */
const REFRESH_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;

/** Re-authenticate a little early, to absorb clock skew (as wms/api/auth.js). */
const TOKEN_SKEW_MS = 60 * 1000;

export interface DeviceCredential {
  clientId: string;
  secretKey: string;
  /** ISO — the credential's hard expiry, server-assigned. */
  expiresAt: string;
  /** Which install this was minted for; a mismatch means a restored backup. */
  installId: string;
  /** Which user and organisation it speaks for. Enforced on read. */
  userId: number;
  organisationId: number;
  alias: string | null;
}

/**
 * The outcome of a token exchange. The three cases are genuinely different
 * decisions, so they are three cases and not a nullable token:
 *
 *   - `ok` — work normally;
 *   - `offline` — keep working from cache and try again when signal returns.
 *     This is the NORMAL state in a block, not an error;
 *   - `rejected` — the credential is revoked or expired server-side. Nothing
 *     this device can do will fix it; it needs a sign-in.
 */
export type DeviceAuthResult =
  | { outcome: "ok"; bearer: string; expiry: number | null }
  | { outcome: "offline" }
  | { outcome: "rejected" };

/**
 * Does this tenant app have an offline story at all? One signal
 * (`tenant/fieldApp.ts`) decides this and the service worker's envelope — two
 * answers to "is this used away from a desk" that must never disagree.
 */
export function tenantSupportsDeviceEnrolment(modules: Pick<ResolvedMfe, "pathPrefix">[]): boolean {
  return isFieldApp(modules);
}

export async function loadDeviceCredential(): Promise<DeviceCredential | null> {
  try {
    const stored = localStorage.getItem(CREDENTIAL_KEY);
    if (!stored) return null;
    const envelope = await decryptJson<DeviceCredential>(stored);
    if (!envelope?.clientId || !envelope.secretKey) return null;
    return envelope;
  } catch {
    return null;
  }
}

async function saveDeviceCredential(credential: DeviceCredential): Promise<void> {
  localStorage.setItem(CREDENTIAL_KEY, await encryptJson(credential));
}

/**
 * Drop this device's credential and its encryption key.
 *
 * Does NOT call `DELETE /boer/devices/:id`: a sign-out is often the moment the
 * network is worst, and a revoke that fails silently is worse than one the
 * grower makes deliberately in Settings. The row ages out at `expiresAt`, and
 * the secret needed to use it is gone from this device either way.
 */
export async function clearDeviceCredential(): Promise<void> {
  try {
    localStorage.removeItem(CREDENTIAL_KEY);
  } catch {
    // best-effort
  }
  await destroyKey();
}

/**
 * The stored credential IF it belongs to this user, organisation and install,
 * and has not expired. Anything else returns null — the caller signs in online
 * as normal.
 */
export async function usableDeviceCredential(
  userId: number | null | undefined,
  organisationId?: number | null,
): Promise<DeviceCredential | null> {
  const credential = await loadDeviceCredential();
  if (!credential) return null;
  if (userId != null && credential.userId !== userId) return null;
  if (organisationId != null && credential.organisationId !== organisationId) return null;
  // A credential restored onto another handset (an Android backup) would
  // authenticate, but its server row belongs to the original install — so
  // rotation and revocation would target a device this is not. Re-enrol
  // instead; the grower notices nothing.
  if (credential.installId !== getInstallId()) return null;
  if (new Date(credential.expiresAt).getTime() <= Date.now()) return null;
  return credential;
}

interface EnrollResponse {
  message: string;
  result: { clientId: string; secretKey: string; expiresAt: string; rotated: boolean };
}

/**
 * Enrol this device for the signed-in grower. Idempotent server-side: called on
 * every online sign-in, it rotates the same row rather than adding one.
 *
 * Returns null instead of throwing on every failure path — enrolment is an
 * upgrade to a session that already works, so nothing here may interrupt a
 * sign-in. An un-enrolled device is exactly today's behaviour.
 */
export async function enrolThisDevice(params: {
  bearer: string;
  orgToken: string;
  userId: number;
  organisationId: number;
  alias?: string;
}): Promise<DeviceCredential | null> {
  if (!isSecureStoreAvailable()) return null;
  const installId = getInstallId();
  try {
    const body = await apiRequest<EnrollResponse>("/boer/devices/enroll", {
      method: "POST",
      bearer: params.bearer,
      orgToken: params.orgToken,
      body: {
        installId,
        ...(params.alias ? { alias: params.alias } : {}),
        ...describeThisDevice(),
      },
    });
    const credential: DeviceCredential = {
      clientId: body.result.clientId,
      secretKey: body.result.secretKey,
      expiresAt: body.result.expiresAt,
      installId,
      userId: params.userId,
      organisationId: params.organisationId,
      alias: params.alias ?? null,
    };
    await saveDeviceCredential(credential);
    return credential;
  } catch (e) {
    if (e instanceof SecureStoreUnavailableError) return null;
    return null;
  }
}

/**
 * Exchange the credential for a 12h token (`POST /public/v2/auth`, the same
 * endpoint every WMS handheld uses).
 *
 * ⚠️ That endpoint is rate-limited to 5/min per credential
 * (`tags: ["RateLimit_Login"]`), which is why the caller must back off rather
 * than retry in a loop — see `deviceSession.ts`.
 */
export async function exchangeDeviceCredential(credential: DeviceCredential): Promise<DeviceAuthResult> {
  try {
    const body = await apiRequest<{ accessToken: string; type: string; expiry: number }>(
      "/public/v2/auth",
      {
        method: "POST",
        body: { clientId: credential.clientId, secretKey: credential.secretKey },
      },
    );
    if (!body?.accessToken) return { outcome: "offline" };
    return {
      outcome: "ok",
      bearer: `Bearer ${body.accessToken}`,
      expiry: typeof body.expiry === "number" ? body.expiry - TOKEN_SKEW_MS : null,
    };
  } catch (e) {
    // 401 is the server saying this credential is revoked, inactive or past its
    // expiry — definitive, and the only case that should end the device
    // session. Everything else (a thrown fetch, a 5xx, a captive portal) is
    // "not right now".
    if (e instanceof ApiRequestError && e.status === 401) return { outcome: "rejected" };
    return { outcome: "offline" };
  }
}

/** True once the credential is close enough to expiry to be worth renewing. */
export function needsRenewal(credential: DeviceCredential, now = Date.now()): boolean {
  const expiresAt = new Date(credential.expiresAt).getTime();
  if (Number.isNaN(expiresAt)) return true;
  return expiresAt - now < REFRESH_WITHIN_MS;
}

/**
 * Push the 30 days out again, authenticated with the DEVICE token — the device
 * saying "I am still in use", which is the only claim that should extend it.
 * Best-effort: a failure leaves the existing credential exactly as it was.
 */
export async function renewDeviceCredential(
  credential: DeviceCredential,
  deviceBearer: string,
): Promise<DeviceCredential | null> {
  try {
    const body = await apiRequest<EnrollResponse>("/boer/devices/refresh", {
      method: "POST",
      bearer: deviceBearer,
    });
    const renewed: DeviceCredential = {
      ...credential,
      clientId: body.result.clientId,
      secretKey: body.result.secretKey,
      expiresAt: body.result.expiresAt,
    };
    await saveDeviceCredential(renewed);
    return renewed;
  } catch {
    return null;
  }
}

/** The subset of `navigator.userAgentData` this reads (Chromium only). */
interface UaData {
  mobile?: boolean;
  platform?: string;
}

/**
 * A name a grower recognises — "Android phone · Chrome", "iPhone · Safari" —
 * from the user agent. `navigator.platform` is NOT a name: Android Chrome
 * reports "Linux armv8l" there, which is what Settings showed (BOE-78).
 * Coarse on purpose: kind of device and browser, nothing that fingerprints.
 */
export function describeDeviceName(ua: string, uaData?: UaData | null): string | null {
  const mobile = uaData?.mobile ?? /Mobi/.test(ua);
  const platform = uaData?.platform ?? "";
  const device =
    /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) || platform === "Android" ? (mobile ? "Android phone" : "Android tablet")
    : /CrOS/.test(ua) || platform === "Chrome OS" ? "Chromebook"
    : /Windows/.test(ua) || platform === "Windows" ? "Windows PC"
    : /Macintosh|Mac OS X/.test(ua) || platform === "macOS" ? "Mac"
    : /Linux/.test(ua) || platform === "Linux" ? "Linux PC"
    : null;
  const browser =
    /EdgA?\/|EdgiOS/.test(ua) ? "Edge"
    : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /Firefox|FxiOS/.test(ua) ? "Firefox"
    : /CriOS|Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    : null;
  if (!device) return browser;
  return browser ? `${device} · ${browser}` : device;
}

/**
 * What the phone tells the server about itself, for the Settings → Devices
 * list. Coarse on purpose: enough for a grower to recognise which phone a row
 * is, and nothing that would make the list a fingerprinting record.
 */
function describeThisDevice(): { model?: string; osVersion?: string } {
  const nav = typeof navigator !== "undefined" ? navigator : null;
  const ua = nav?.userAgent ?? "";
  const uaData = nav && "userAgentData" in nav ? (nav as { userAgentData?: UaData }).userAgentData : null;
  const platform = nav && "platform" in nav ? String(nav.platform || "") : "";
  const model = describeDeviceName(ua, uaData) || platform;
  const os =
    /Android[ /]?([\d.]+)?/.exec(ua)?.[0] ||
    /(iPhone|iPad) OS ([\d_]+)/.exec(ua)?.[0]?.replace(/_/g, ".") ||
    /(Windows NT [\d.]+|Mac OS X [\d_.]+|Linux)/.exec(ua)?.[0]?.replace(/_/g, ".") ||
    "";
  return {
    ...(model ? { model } : {}),
    ...(os ? { osVersion: os } : {}),
  };
}
